import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Loader2, AlertCircle, Mail } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const INTERESTS = [
  { value: 'podcast', label: 'Podcast' },
  { value: 'fotografia', label: 'Fotografia' },
  { value: 'video', label: 'Vídeo' },
  { value: 'conteudo', label: 'Conteúdo' },
  { value: 'outro', label: 'Outro' },
];

/**
 * WaitlistSection — "O projeto está chegando"
 * Formulário real → POST /api/waitlist (MongoDB).
 * Estados: idle / submitting / success / error.
 */
const WaitlistSection = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    whatsapp: '',
    interest: 'podcast',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    try {
      await axios.post(`${API}/api/waitlist`, form);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        setErrorMsg(detail[0]?.msg || 'Verifique os dados e tente novamente.');
      } else if (typeof detail === 'string') {
        setErrorMsg(detail);
      } else {
        setErrorMsg('Não foi possível enviar. Tente novamente em instantes.');
      }
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden bg-gradient-to-b from-[#0B0B0F] via-[#0B0B0F] to-[#050508]"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(37,64,163,0.18)_0%,rgba(0,0,0,0)_60%)]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(37,64,163,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(37,64,163,0.6) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-12"
        >
          <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] font-bold text-[#F59E0B] mb-4 inline-block">
            O Projeto Está Chegando
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-black text-[#F5F5F0] leading-[1.05] mb-5">
            Coutin Studio.{' '}
            <span className="text-[#2540A3] italic">Chegando em 2027.</span>
          </h2>
          <p className="text-base md:text-lg text-[#A1A1AA] font-light leading-relaxed max-w-2xl mx-auto">
            Estamos construindo um novo espaço para quem deseja criar com mais
            qualidade, personalidade e possibilidades. Acompanhe essa construção e
            seja uma das primeiras pessoas a conhecer o Coutin Studio.
          </p>
        </motion.div>

        {status === 'success' ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            data-testid="waitlist-success"
            className="rounded-2xl border border-[#2540A3]/40 bg-[#2540A3]/10 backdrop-blur-md p-10 md:p-14 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-[#2540A3] flex items-center justify-center mx-auto mb-5 shadow-[0_10px_40px_-5px_rgba(37,64,163,0.6)]">
              <Check className="w-8 h-8 text-white" strokeWidth={3} />
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-[#F5F5F0] mb-3">
              Você está na lista.
            </h3>
            <p className="text-base md:text-lg text-[#A1A1AA] font-light max-w-md mx-auto">
              Vamos te avisar sobre cada passo da construção do Coutin Studio —
              equipamentos, cenários e o dia da inauguração em 2027.
            </p>
            <p className="text-sm text-[#8A8A8A] mt-6 flex items-center justify-center gap-2">
              <Mail className="w-3.5 h-3.5" />
              Fique de olho no e-mail cadastrado.
            </p>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={handleSubmit}
            data-testid="waitlist-form"
            className="rounded-2xl border border-white/10 bg-[#141419]/70 backdrop-blur-xl p-6 md:p-10 space-y-5"
          >
            {/* Name */}
            <div>
              <label htmlFor="wl-name" className="block text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A] font-bold mb-2">
                Nome
              </label>
              <input
                id="wl-name"
                name="name"
                type="text"
                required
                minLength={2}
                value={form.name}
                onChange={handleChange}
                placeholder="Como podemos te chamar?"
                data-testid="waitlist-input-name"
                className="w-full bg-[#0B0B0F] border border-white/10 focus:border-[#2540A3] rounded-lg px-4 py-3 text-[#F5F5F0] placeholder-[#5A5A5A] outline-none transition-colors"
              />
            </div>

            {/* Email + WhatsApp */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="wl-email" className="block text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A] font-bold mb-2">
                  E-mail
                </label>
                <input
                  id="wl-email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="seu@email.com"
                  data-testid="waitlist-input-email"
                  className="w-full bg-[#0B0B0F] border border-white/10 focus:border-[#2540A3] rounded-lg px-4 py-3 text-[#F5F5F0] placeholder-[#5A5A5A] outline-none transition-colors"
                />
              </div>
              <div>
                <label htmlFor="wl-wpp" className="block text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A] font-bold mb-2">
                  WhatsApp
                </label>
                <input
                  id="wl-wpp"
                  name="whatsapp"
                  type="tel"
                  required
                  value={form.whatsapp}
                  onChange={handleChange}
                  placeholder="(45) 99999-9999"
                  data-testid="waitlist-input-whatsapp"
                  className="w-full bg-[#0B0B0F] border border-white/10 focus:border-[#2540A3] rounded-lg px-4 py-3 text-[#F5F5F0] placeholder-[#5A5A5A] outline-none transition-colors"
                />
              </div>
            </div>

            {/* Interest */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A] font-bold mb-2">
                Interesse principal
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {INTERESTS.map((opt) => {
                  const active = form.interest === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setForm({ ...form, interest: opt.value })}
                      data-testid={`waitlist-interest-${opt.value}`}
                      className={`text-xs md:text-sm px-3 py-2.5 rounded-lg border font-medium transition-all ${
                        active
                          ? 'border-[#2540A3] bg-[#2540A3]/20 text-[#F5F5F0]'
                          : 'border-white/10 text-[#A1A1AA] hover:border-white/25 hover:text-[#F5F5F0]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Optional message */}
            <div>
              <label htmlFor="wl-msg" className="block text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A] font-bold mb-2">
                Mensagem <span className="text-[#5A5A5A]">(opcional)</span>
              </label>
              <textarea
                id="wl-msg"
                name="message"
                rows={3}
                maxLength={500}
                value={form.message}
                onChange={handleChange}
                placeholder="Conte um pouco sobre o que você tem em mente..."
                data-testid="waitlist-input-message"
                className="w-full bg-[#0B0B0F] border border-white/10 focus:border-[#2540A3] rounded-lg px-4 py-3 text-[#F5F5F0] placeholder-[#5A5A5A] outline-none transition-colors resize-none"
              />
            </div>

            {/* Error */}
            {status === 'error' && (
              <div
                data-testid="waitlist-error"
                className="flex items-start gap-2 text-sm text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg px-4 py-3"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            {/* Submit */}
            <motion.button
              type="submit"
              disabled={status === 'submitting'}
              whileHover={{ scale: status === 'submitting' ? 1 : 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="waitlist-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#2540A3] hover:bg-[#1A3690] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wide text-sm px-6 py-4 rounded-lg shadow-[0_20px_60px_-15px_rgba(37,64,163,0.6)] transition-all duration-300"
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Quero estar na lista'
              )}
            </motion.button>

            <p className="text-[11px] text-[#5A5A5A] text-center leading-relaxed">
              Ao enviar, você concorda em receber comunicações da Coutin sobre o
              lançamento do Coutin Studio. Você pode se descadastrar a qualquer
              momento.
            </p>
          </motion.form>
        )}
      </div>
    </section>
  );
};

export default WaitlistSection;
